import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Target, Heart, Globe, Sparkles, Users, Lightbulb,
  Rocket, Shield, BookOpen, Beaker, Zap, Award, Eye,
  TrendingUp, CheckCircle2, Star, Mail
} from 'lucide-react';
import landingService from '../../../services/landingService';
import CompanyLayout from './CompanyLayout';
import './AboutPage.css';

const AboutPage = () => {
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [stats, setStats] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    );

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Fetch dynamic data from API
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const [statsRes, milestonesRes, teamRes] = await Promise.all([
        landingService.getPublicStats(),
        landingService.getMilestones(),
        landingService.getTeamMembers(),
      ]);
      if (statsRes.success && statsRes.stats) {
        const s = statsRes.stats;
        setStats([
          { value: typeof s.totalResearchers === 'number' ? s.totalResearchers.toLocaleString() + '+' : s.totalResearchers, label: 'Researchers' },
          { value: s.totalPapers || '200M+', label: 'Papers Indexed' },
          { value: (s.totalInstitutions || 200) + '+', label: 'Institutions' },
          { value: s.platformUptime || '99.9%', label: 'Uptime' },
        ]);
      }
      if (milestonesRes.success) setMilestones(milestonesRes.milestones || []);
      if (teamRes.success) setTeam(teamRes.team || teamRes.members || []);
      setLoading(false);
    };
    fetchData();
  }, []);

  const isVisible = (id) => visibleSections.has(id);

  const values = [
    {
      icon: Globe,
      title: 'Open by Default',
      description: 'We believe research belongs to everyone. Our platform is designed to break down silos and make academic knowledge accessible worldwide.',
    },
    {
      icon: Shield,
      title: 'Privacy First',
      description: 'End-to-end encryption, zero-knowledge architecture for private messages, and full data sovereignty. Your research is yours.',
    },
    {
      icon: Users,
      title: 'Community Driven',
      description: 'Every feature we build starts with researcher feedback. Our roadmap is shaped by the 50,000+ researchers who use the platform daily.',
    },
    {
      icon: Sparkles,
      title: 'AI as a Tool, Not a Replacement',
      description: 'AI should amplify human expertise, not replace it. Our AI features help you discover, organize, and connect — never fabricate.',
    },
  ];

  return (
    <CompanyLayout>
      {/* ── Hero ── */}
      <section className="rsc-hero about-hero">
        <div className="rsc-hero-inner">
          <h1 className="rsc-hero-title">
            Accelerating research,<br />together
          </h1>
          <p className="rsc-hero-subtitle">
            We're building the platform we wish existed when we were researchers — 
            one that makes collaboration effortless, discovery instant, and impact measurable.
          </p>
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="about-stats-section">
        <div className="about-stats-inner">
          {stats.map((stat, i) => (
            <div key={i} className="about-stat">
              <span className="about-stat-value">{stat.value}</span>
              <span className="about-stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Mission ── */}
      <section
        id="about-mission"
        className={`rsc-section ${isVisible('about-mission') ? 'about-visible' : ''}`}
        ref={(el) => (sectionRefs.current['about-mission'] = el)}
      >
        <div className="rsc-section-inner about-mission-inner">
          <div className="about-mission-content">
            <span className="about-mission-eyebrow">Why we exist</span>
            <h2 className="rsc-section-title about-mission-title">Our Mission</h2>
            <p className="about-mission-text">
              Research is the engine of human progress, yet the tools researchers use 
              haven't evolved in decades. Scattered PDFs, siloed teams, opaque peer review, 
              and fragmented communication slow down the very people pushing knowledge forward.
            </p>
            <p className="about-mission-text">
              <strong>ResearchPro</strong> exists to change that. We're building an all-in-one 
              platform where researchers can discover papers, form teams, collaborate in real-time, 
              share findings, and accelerate discoveries — all in one place.
            </p>
            <p className="about-mission-text about-mission-highlight">
              Our goal is to cut the time from question to published answer in half.
            </p>

            {/* Founder + North Star */}
            <div className="about-mission-founder">
              <div className="about-mission-founder-row">
                <div className="about-mission-founder-badge">
                  <span className="about-mission-badge-initials">MC</span>
                </div>
                <div className="about-mission-founder-meta">
                  <span className="about-mission-ceo-name">Mohammad Mehran Chaudhary</span>
                  <span className="about-mission-ceo-tag">Founder & CEO</span>
                </div>
              </div>
              <div className="about-mission-star-card">
                <Target size={16} className="about-mission-star-icon" />
                <span className="about-mission-star-label">Our North Star</span>
                <span className="about-mission-star-text">
                  Make research collaboration as seamless as conversation.
                </span>
              </div>
            </div>
          </div>

          {/* Photo */}
          <div className="about-mission-visual">
            <div className="about-mission-photo-card">
              <img 
                src={`${process.env.PUBLIC_URL}/team/mehran-ceo.png`}
                alt="Mohammad Mehran Chaudhary — Founder & CEO"
                className="about-mission-ceo-photo"
              />
              <div className="about-mission-photo-overlay">
                <span className="about-mission-photo-name">Mehran Chaudhary</span>
                <span className="about-mission-photo-role">Founder & CEO</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Values ── */}
      <section
        id="about-values"
        className={`rsc-section ${isVisible('about-values') ? 'about-visible' : ''}`}
        ref={(el) => (sectionRefs.current['about-values'] = el)}
      >
        <div className="rsc-section-inner">
          <h2 className="rsc-section-title">What We Believe</h2>
          <p className="rsc-section-subtitle">The principles that guide every decision we make.</p>

          <div className="rsc-grid--2 rsc-grid">
            {values.map((value, i) => (
              <div key={i} className="rsc-card about-value-card">
                <div className="rsc-card-icon">
                  <value.icon size={22} />
                </div>
                <h3 className="rsc-card-title">{value.title}</h3>
                <p className="rsc-card-desc">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Timeline ── */}
      <section
        id="about-timeline"
        className={`rsc-section ${isVisible('about-timeline') ? 'about-visible' : ''}`}
        ref={(el) => (sectionRefs.current['about-timeline'] = el)}
      >
        <div className="rsc-section-inner">
          <h2 className="rsc-section-title">Our Journey</h2>
          <p className="rsc-section-subtitle">From an idea to 50,000+ researchers worldwide.</p>

          <div className="about-timeline">
            {milestones.map((m, i) => (
              <div key={m._id || i} className="about-timeline-item">
                <div className="about-timeline-marker">
                  <div className="about-timeline-dot" />
                  {i < milestones.length - 1 && <div className="about-timeline-line" />}
                </div>
                <div className="about-timeline-content">
                  <span className="about-timeline-year">{m.year}</span>
                  <h3 className="about-timeline-title">{m.title}</h3>
                  <p className="about-timeline-desc">{m.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Team (hidden) ── */}
      {/* <section
        id="about-team"
        className={`rsc-section ${isVisible('about-team') ? 'about-visible' : ''}`}
        ref={(el) => (sectionRefs.current['about-team'] = el)}
      >
        <div className="rsc-section-inner">
          <h2 className="rsc-section-title">The Team</h2>
          <p className="rsc-section-subtitle">Researchers, engineers, and builders who care deeply about science.</p>

          <div className="rsc-grid">
            {team.map((person, i) => {
              const initials = person.initials || person.name?.split(' ').map((n) => n[0]).join('') || '?';
              const color = person.color || '#7C4DFF';
              return (
                <div key={person._id || i} className="rsc-card about-team-card">
                  {person.photoUrl ? (
                    <img src={person.photoUrl} alt={person.name} className="about-team-avatar" style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover' }} />
                  ) : (
                    <div className="about-team-avatar" style={{ background: `linear-gradient(135deg, ${color}, ${color}99)` }}>
                      {initials}
                    </div>
                  )}
                  <h3 className="rsc-card-title">{person.name}</h3>
                  <span className="about-team-role">{person.role}</span>
                  <p className="rsc-card-desc">{person.bio}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section> */}

      {/* ── CTA ── */}
      <section
        id="about-cta"
        ref={(el) => (sectionRefs.current['about-cta'] = el)}
        className={`rsc-cta ${isVisible('about-cta') ? 'rsc-cta--visible' : ''}`}
      >
        <div className="rsc-cta-inner">
          <span className="rsc-cta-badge">
            <Zap size={14} />
            Join the Movement
          </span>
          <h2 className="rsc-cta-title">Join us in reshaping research</h2>
          <p className="rsc-cta-subtitle">
            Whether you're a researcher looking for a better platform or 
            a builder who wants to help create it — we'd love to hear from you.
          </p>
          <div className="rsc-cta-actions">
            <Link to="/signup" className="rsc-btn rsc-btn--primary">
              Start Researching
              <ArrowRight size={16} />
            </Link>
            <Link to="/careers" className="rsc-btn rsc-btn--secondary">
              View Open Roles
            </Link>
          </div>
        </div>
      </section>
    </CompanyLayout>
  );
};

export default AboutPage;
